import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Archive,
  CalendarClock,
  CheckCircle2,
  ChevronRight,
  CircleGauge,
  FileCheck2,
  FileText,
  Filter,
  FlaskConical,
  Loader2,
  PackageCheck,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  Upload,
  X,
} from "lucide-react";
import { useLabPermissions } from "../../hooks/useLabPermissions";
import {
  createReferenceStandardCapability,
  createReferenceStandardAsset,
  createReferenceStandardCertificate,
  getReferenceCertificateSignedUrl,
  isReferenceStandardsMigrationMissing,
  listReferenceStandardAssets,
  updateReferenceStandardAsset,
  updateReferenceStandardCapability,
} from "../../utils/api-reference-standards";

const FAMILY_OPTIONS = [
  ["pressure", "Presión"],
  ["temperature", "Temperatura"],
  ["electrical", "Eléctrica"],
  ["flow", "Caudal"],
  ["dimensional", "Dimensional"],
  ["analytical", "Analítica"],
  ["gas_concentration", "Gases / concentración"],
  ["environmental", "Ambientales"],
  ["mass", "Masa"],
  ["torque", "Torque"],
  ["rotation", "Rotación"],
];

const FAMILY_LABELS = Object.fromEntries(FAMILY_OPTIONS);

const STATUS_META = {
  active: {
    label: "Activo",
    className: "border-emerald-500/30 bg-emerald-500/10 text-emerald-200",
  },
  inactive: {
    label: "No activo",
    className: "border-amber-500/30 bg-amber-500/10 text-amber-200",
  },
  retired: {
    label: "Baja",
    className: "border-slate-500/30 bg-slate-500/10 text-slate-300",
  },
};

const REVIEW_META = {
  verified: {
    label: "Verificado",
    className: "border-sky-500/30 bg-sky-500/10 text-sky-200",
  },
  pending: {
    label: "Pendiente",
    className: "border-amber-500/30 bg-amber-500/10 text-amber-200",
  },
  needs_attention: {
    label: "Revisar coincidencia",
    className: "border-rose-500/30 bg-rose-500/10 text-rose-200",
  },
};

const INITIAL_ASSET_FORM = {
  code: "",
  family: "temperature",
  name: "",
  asset_kind: "equipment",
  operational_status: "active",
  brand_model: "",
  serial: "",
  service_description: "",
  calibration_frequency_months: "",
  calibration_provider: "",
  estimated_lead_time: "",
  capability_role: "reference",
  range_min: "",
  range_max: "",
  unit: "",
  resolution: "",
};

const INITIAL_CERTIFICATE_FORM = {
  certificate_no: "",
  document_type: "calibration_certificate",
  issuer: "",
  accreditation: "",
  calibration_date: "",
  issue_date: "",
  valid_until: "",
  certificate_status: "draft",
  coverage_factor: "2",
  confidence_level: "95",
  traceability: "",
  capability_id: "",
  result_unit: "°C",
  interpolation_policy: "exact_points",
  reference_input_mode: "corrected_value",
  result_rows: "",
  notes: "",
};

function parseCertificateResultRows(value, form) {
  const number = (raw) => {
    const text = String(raw ?? "").trim().replace(",", ".");
    if (!text) return null;
    const parsed = Number(text);
    if (!Number.isFinite(parsed)) {
      throw new Error(`Valor numérico inválido en resultados: "${raw}".`);
    }
    return parsed;
  };
  return String(value || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const columns = line.includes("\t") ? line.split("\t") : line.split(";");
      if (columns.length < 4) {
        throw new Error(
          "Cada resultado debe tener 4 columnas: referencia; indicación; corrección; U."
        );
      }
      return {
        reference_value: number(columns[0]),
        indicated_value: number(columns[1]),
        correction: number(columns[2]),
        uncertainty_expanded: number(columns[3]),
        unit: form.result_unit,
        interpolation_policy: form.interpolation_policy,
      };
    });
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("es-AR").format(date);
}

function daysUntil(value) {
  if (!value) return null;
  const target = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(target.getTime())) return null;
  return Math.ceil((target.getTime() - Date.now()) / 86400000);
}

function certificatesFor(asset) {
  return (asset?.certificate_links || [])
    .map((link) => ({ ...link.certificate, asset_role: link.asset_role }))
    .filter((certificate) => certificate?.id)
    .sort((a, b) =>
      String(b.calibration_date || b.issue_date || b.created_at || "").localeCompare(
        String(a.calibration_date || a.issue_date || a.created_at || "")
      )
    );
}

function primaryCode(asset) {
  const codes = [...(asset?.codes || [])].sort(
    (a, b) => Number(b.is_primary) - Number(a.is_primary)
  );
  return codes[0]?.code || "Sin código";
}

function primaryFamily(asset) {
  const code = (asset?.codes || []).find((item) => item.is_primary);
  return code?.family || asset?.codes?.[0]?.family || "";
}

function Badge({ meta }) {
  if (!meta) return null;
  return (
    <span
      className={`inline-flex whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-medium ${meta.className}`}
    >
      {meta.label}
    </span>
  );
}

function Metric({ icon: Icon, label, value, tone = "slate" }) {
  const tones = {
    emerald: "border-emerald-500/20 bg-emerald-500/10 text-emerald-200",
    sky: "border-sky-500/20 bg-sky-500/10 text-sky-200",
    amber: "border-amber-500/20 bg-amber-500/10 text-amber-200",
    rose: "border-rose-500/20 bg-rose-500/10 text-rose-200",
    slate: "border-white/10 bg-white/[0.035] text-slate-200",
  };
  return (
    <div className={`rounded-xl border p-4 ${tones[tone]}`}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-2xl font-semibold text-white">{value}</div>
          <div className="mt-0.5 text-xs opacity-80">{label}</div>
        </div>
        <Icon size={22} className="opacity-80" aria-hidden="true" />
      </div>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block space-y-1.5 ${className}`}>
      <span className="text-xs font-medium uppercase tracking-wide text-slate-400">
        {label}
      </span>
      {children}
    </label>
  );
}

const inputClass =
  "w-full rounded-lg border border-white/10 bg-slate-950/70 px-3 py-2 text-sm text-white outline-none transition focus:border-emerald-400/60 focus:ring-2 focus:ring-emerald-400/10";

function Modal({ title, subtitle, onClose, children, maxWidth = "max-w-3xl" }) {
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
      <div
        className={`max-h-[92vh] w-full ${maxWidth} overflow-y-auto rounded-2xl border border-white/10 bg-slate-900 shadow-2xl`}
      >
        <header className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-white/10 bg-slate-900/95 px-5 py-4 backdrop-blur">
          <div>
            <h2 className="text-lg font-semibold text-white">{title}</h2>
            {subtitle && <p className="mt-1 text-sm text-slate-400">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-400 hover:bg-white/5 hover:text-white"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}

function AssetFormModal({ busy, onClose, onSubmit }) {
  const [form, setForm] = useState(INITIAL_ASSET_FORM);
  const set = (field, value) =>
    setForm((current) => ({ ...current, [field]: value }));

  const submit = (event) => {
    event.preventDefault();
    onSubmit(form);
  };

  return (
    <Modal
      title="Nuevo instrumento patrón"
      subtitle="El registro quedará pendiente de revisión hasta que se controle su documentación."
      onClose={onClose}
    >
      <form onSubmit={submit} className="space-y-5 p-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Código interno">
            <input
              className={inputClass}
              value={form.code}
              onChange={(event) => set("code", event.target.value)}
              placeholder="T-011"
              required
            />
          </Field>
          <Field label="Magnitud">
            <select
              className={inputClass}
              value={form.family}
              onChange={(event) => set("family", event.target.value)}
            >
              {FAMILY_OPTIONS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Equipo o material" className="sm:col-span-2">
            <input
              className={inputClass}
              value={form.name}
              onChange={(event) => set("name", event.target.value)}
              required
            />
          </Field>
          <Field label="Tipo">
            <select
              className={inputClass}
              value={form.asset_kind}
              onChange={(event) => set("asset_kind", event.target.value)}
            >
              <option value="equipment">Equipo</option>
              <option value="reference_material">Material de referencia</option>
              <option value="standard_gas">Gas patrón</option>
              <option value="accessory">Accesorio</option>
            </select>
          </Field>
          <Field label="Estado">
            <select
              className={inputClass}
              value={form.operational_status}
              onChange={(event) => set("operational_status", event.target.value)}
            >
              <option value="active">Activo</option>
              <option value="inactive">No activo</option>
              <option value="retired">Baja</option>
            </select>
          </Field>
          <Field label="Marca / Modelo">
            <input
              className={inputClass}
              value={form.brand_model}
              onChange={(event) => set("brand_model", event.target.value)}
            />
          </Field>
          <Field label="Número de serie / lote">
            <input
              className={inputClass}
              value={form.serial}
              onChange={(event) => set("serial", event.target.value)}
            />
          </Field>
          <Field label="Prestación" className="sm:col-span-2">
            <textarea
              className={`${inputClass} min-h-20`}
              value={form.service_description}
              onChange={(event) => set("service_description", event.target.value)}
            />
          </Field>
          <Field label="Rol metrológico">
            <select
              className={inputClass}
              value={form.capability_role}
              onChange={(event) => set("capability_role", event.target.value)}
            >
              <option value="reference">Sistema de referencia</option>
              <option value="source">Fuente</option>
              <option value="measurement">Medición</option>
              <option value="generation">Generación</option>
              <option value="environment">Ambiental</option>
              <option value="auxiliary">Auxiliar</option>
            </select>
          </Field>
          <Field label="Unidad">
            <input
              className={inputClass}
              value={form.unit}
              onChange={(event) => set("unit", event.target.value)}
              placeholder="°C, bar, V…"
            />
          </Field>
          <Field label="Rango mínimo">
            <input
              type="number"
              step="any"
              className={inputClass}
              value={form.range_min}
              onChange={(event) => set("range_min", event.target.value)}
            />
          </Field>
          <Field label="Rango máximo">
            <input
              type="number"
              step="any"
              className={inputClass}
              value={form.range_max}
              onChange={(event) => set("range_max", event.target.value)}
            />
          </Field>
          <Field label="Resolución">
            <input
              type="number"
              step="any"
              className={inputClass}
              value={form.resolution}
              onChange={(event) => set("resolution", event.target.value)}
            />
          </Field>
          <Field label="Frecuencia (meses)">
            <input
              type="number"
              min="1"
              className={inputClass}
              value={form.calibration_frequency_months}
              onChange={(event) =>
                set("calibration_frequency_months", event.target.value)
              }
            />
          </Field>
          <Field label="Organismo de calibración">
            <input
              className={inputClass}
              value={form.calibration_provider}
              onChange={(event) => set("calibration_provider", event.target.value)}
            />
          </Field>
          <Field label="Plazo estimado">
            <input
              className={inputClass}
              value={form.estimated_lead_time}
              onChange={(event) => set("estimated_lead_time", event.target.value)}
            />
          </Field>
        </div>
        <div className="flex justify-end gap-3 border-t border-white/10 pt-4">
          <button type="button" onClick={onClose} className="btn-ghost">
            Cancelar
          </button>
          <button type="submit" disabled={busy} className="btn-primary">
            {busy && <Loader2 size={16} className="animate-spin" />}
            Guardar patrón
          </button>
        </div>
      </form>
    </Modal>
  );
}

function CertificateFormModal({ asset, busy, onClose, onSubmit }) {
  const [form, setForm] = useState(() => ({
    ...INITIAL_CERTIFICATE_FORM,
    capability_id: asset?.capabilities?.[0]?.id || "",
    result_unit: asset?.capabilities?.[0]?.unit || "°C",
  }));
  const [file, setFile] = useState(null);
  const set = (field, value) =>
    setForm((current) => ({ ...current, [field]: value }));

  return (
    <Modal
      title={`Nuevo certificado · ${primaryCode(asset)}`}
      subtitle={`${asset.name}${asset.serial ? ` · S/N ${asset.serial}` : ""}`}
      onClose={onClose}
    >
      <form
        className="space-y-5 p-5"
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit({ form, file });
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Número de certificado">
            <input
              className={inputClass}
              value={form.certificate_no}
              onChange={(event) => set("certificate_no", event.target.value)}
              required
            />
          </Field>
          <Field label="Tipo de documento">
            <select
              className={inputClass}
              value={form.document_type}
              onChange={(event) => set("document_type", event.target.value)}
            >
              <option value="calibration_certificate">
                Certificado de calibración
              </option>
              <option value="test_report">Informe de ensayo</option>
              <option value="reference_material_certificate">
                Certificado de material de referencia
              </option>
              <option value="certificate_of_analysis">
                Certificado de análisis
              </option>
              <option value="internal_verification">Verificación interna</option>
              <option value="other">Otro</option>
            </select>
          </Field>
          <Field label="Organismo emisor">
            <input
              className={inputClass}
              value={form.issuer}
              onChange={(event) => set("issuer", event.target.value)}
            />
          </Field>
          <Field label="Acreditación">
            <input
              className={inputClass}
              value={form.accreditation}
              onChange={(event) => set("accreditation", event.target.value)}
              placeholder="SAC, INTI, ISO/IEC 17025…"
            />
          </Field>
          <Field label="Fecha de calibración">
            <input
              type="date"
              className={inputClass}
              value={form.calibration_date}
              onChange={(event) => set("calibration_date", event.target.value)}
            />
          </Field>
          <Field label="Fecha de emisión">
            <input
              type="date"
              className={inputClass}
              value={form.issue_date}
              onChange={(event) => set("issue_date", event.target.value)}
            />
          </Field>
          <Field label="Válido hasta">
            <input
              type="date"
              className={inputClass}
              value={form.valid_until}
              onChange={(event) => set("valid_until", event.target.value)}
            />
          </Field>
          <Field label="Estado documental">
            <select
              className={inputClass}
              value={form.certificate_status}
              onChange={(event) =>
                set("certificate_status", event.target.value)
              }
            >
              <option value="draft">Borrador / pendiente</option>
              <option value="verified">Verificado</option>
              <option value="expired">Vencido</option>
              <option value="revoked">Revocado</option>
              <option value="superseded">Reemplazado</option>
            </select>
          </Field>
          <Field label="Factor de cobertura k">
            <input
              type="number"
              step="any"
              className={inputClass}
              value={form.coverage_factor}
              onChange={(event) => set("coverage_factor", event.target.value)}
            />
          </Field>
          <Field label="Nivel de confianza (%)">
            <input
              type="number"
              step="any"
              className={inputClass}
              value={form.confidence_level}
              onChange={(event) => set("confidence_level", event.target.value)}
            />
          </Field>
          <Field label="Trazabilidad" className="sm:col-span-2">
            <textarea
              className={`${inputClass} min-h-16`}
              value={form.traceability}
              onChange={(event) => set("traceability", event.target.value)}
            />
          </Field>
          <Field label="Capacidad certificada" className="sm:col-span-2">
            <select
              className={inputClass}
              value={form.capability_id}
              onChange={(event) => set("capability_id", event.target.value)}
            >
              <option value="">Documento sin resultados numéricos</option>
              {(asset.capabilities || []).map((capability) => (
                <option key={capability.id} value={capability.id}>
                  {capability.function_label || capability.function_key || capability.magnitude}
                  {capability.range_min != null || capability.range_max != null
                    ? ` · ${capability.range_min ?? "—"} a ${capability.range_max ?? "—"} ${capability.unit || ""}`
                    : ""}
                </option>
              ))}
            </select>
          </Field>
          {form.capability_id && (
            <>
              <Field label="Lectura que cargará el técnico">
                <select
                  className={inputClass}
                  value={form.reference_input_mode}
                  onChange={(event) => set("reference_input_mode", event.target.value)}
                >
                  <option value="corrected_value">Valor ya corregido</option>
                  <option value="raw_indication">Indicación sin corregir</option>
                </select>
              </Field>
              <Field label="Uso entre puntos">
                <select
                  className={inputClass}
                  value={form.interpolation_policy}
                  onChange={(event) => set("interpolation_policy", event.target.value)}
                >
                  <option value="exact_points">Solo puntos certificados</option>
                  <option value="linear">Interpolación lineal autorizada</option>
                  <option value="constant">Corrección constante</option>
                </select>
              </Field>
              <Field label="Unidad de resultados">
                <input
                  className={inputClass}
                  value={form.result_unit}
                  onChange={(event) => set("result_unit", event.target.value)}
                />
              </Field>
              <Field label="Resultados certificados" className="sm:col-span-2">
                <textarea
                  className={`${inputClass} min-h-28 font-mono text-xs`}
                  value={form.result_rows}
                  onChange={(event) => set("result_rows", event.target.value)}
                  placeholder={"referencia;indicación;corrección;U\n0,00;-0,10;0,10;0,20\n100,00;99,90;0,10;0,25"}
                />
                <p className="mt-1 text-xs text-slate-500">
                  Una fila por punto. Pegá cuatro columnas separadas por punto y coma o tabulación:
                  valor de referencia, indicación, corrección y U expandida.
                </p>
              </Field>
            </>
          )}
          <Field label="Certificado PDF" className="sm:col-span-2">
            <div className="rounded-xl border border-dashed border-white/15 bg-white/[0.025] p-4">
              <input
                type="file"
                accept="application/pdf,.pdf"
                onChange={(event) => setFile(event.target.files?.[0] || null)}
                className="block w-full text-sm text-slate-300 file:mr-3 file:rounded-lg file:border-0 file:bg-emerald-500/15 file:px-3 file:py-2 file:text-emerald-200 hover:file:bg-emerald-500/25"
              />
              <p className="mt-2 text-xs text-slate-500">
                PDF privado, máximo 25 MB. Nunca reemplaza certificados anteriores.
              </p>
            </div>
          </Field>
          <Field label="Observaciones" className="sm:col-span-2">
            <textarea
              className={`${inputClass} min-h-16`}
              value={form.notes}
              onChange={(event) => set("notes", event.target.value)}
            />
          </Field>
        </div>
        <div className="flex justify-end gap-3 border-t border-white/10 pt-4">
          <button type="button" onClick={onClose} className="btn-ghost">
            Cancelar
          </button>
          <button type="submit" disabled={busy} className="btn-primary">
            {busy ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Upload size={16} />
            )}
            Guardar certificado
          </button>
        </div>
      </form>
    </Modal>
  );
}

function CapabilityEditor({ capability, family, busy, isNew = false, onSave, onCancel }) {
  const [form, setForm] = useState(() => ({
    magnitude: capability?.magnitude || family || "temperature",
    function_key: capability?.function_key || "",
    function_label: capability?.function_label || "",
    capability_role: capability?.capability_role || "reference",
    range_min: capability?.range_min ?? "",
    range_max: capability?.range_max ?? "",
    unit: capability?.unit || "°C",
    resolution: capability?.resolution ?? "",
    active: capability?.active !== false,
  }));
  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  return (
    <form
      className="rounded-xl border border-white/10 bg-white/[0.025] p-4"
      onSubmit={async (event) => {
        event.preventDefault();
        await onSave(form);
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Función" className="sm:col-span-2">
          <input
            className={inputClass}
            value={form.function_label}
            onChange={(event) => set("function_label", event.target.value)}
            placeholder="Ej.: Sistema CPH6600 + Pt100"
            required
          />
        </Field>
        <Field label="Rol">
          <select
            className={inputClass}
            value={form.capability_role}
            onChange={(event) => set("capability_role", event.target.value)}
          >
            <option value="reference">Referencia</option>
            <option value="source">Fuente</option>
            <option value="measurement">Medición</option>
            <option value="generation">Generación</option>
            <option value="environment">Ambiental</option>
            <option value="auxiliary">Auxiliar</option>
          </select>
        </Field>
        <Field label="Magnitud">
          <select
            className={inputClass}
            value={form.magnitude}
            onChange={(event) => set("magnitude", event.target.value)}
          >
            {FAMILY_OPTIONS.map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </Field>
        <Field label="Rango mínimo">
          <input type="number" step="any" className={inputClass} value={form.range_min} onChange={(event) => set("range_min", event.target.value)} />
        </Field>
        <Field label="Rango máximo">
          <input type="number" step="any" className={inputClass} value={form.range_max} onChange={(event) => set("range_max", event.target.value)} />
        </Field>
        <Field label="Unidad">
          <input className={inputClass} value={form.unit} onChange={(event) => set("unit", event.target.value)} />
        </Field>
        <Field label="Resolución">
          <input type="number" step="any" className={inputClass} value={form.resolution} onChange={(event) => set("resolution", event.target.value)} />
        </Field>
      </div>
      <div className="mt-3 flex items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-xs text-slate-300">
          <input type="checkbox" checked={form.active} onChange={(event) => set("active", event.target.checked)} />
          Capacidad activa
        </label>
        <div className="flex gap-2">
          {isNew && (
            <button type="button" className="btn-ghost" onClick={onCancel}>Cancelar</button>
          )}
          <button type="submit" disabled={busy} className="btn-primary">
            {busy && <Loader2 size={15} className="animate-spin" />}
            {isNew ? "Agregar capacidad" : "Guardar alcance"}
          </button>
        </div>
      </div>
    </form>
  );
}

function AssetDetailModal({
  asset,
  busy,
  onClose,
  onReload,
  onAddCertificate,
  onOpenCertificate,
}) {
  const certificates = certificatesFor(asset);
  const [addingCapability, setAddingCapability] = useState(false);
  const update = async (patch) => {
    await updateReferenceStandardAsset(asset.id, patch);
    await onReload();
  };
  const saveCapability = async (capability, patch) => {
    await updateReferenceStandardCapability(capability.id, patch);
    await onReload();
  };
  const addCapability = async (patch) => {
    await createReferenceStandardCapability(asset.id, patch);
    setAddingCapability(false);
    await onReload();
  };

  return (
    <Modal
      title={`${primaryCode(asset)} · ${asset.name}`}
      subtitle={
        asset.brand_model
          ? `${asset.brand_model}${asset.serial ? ` · S/N ${asset.serial}` : ""}`
          : asset.serial
            ? `S/N ${asset.serial}`
            : null
      }
      onClose={onClose}
      maxWidth="max-w-5xl"
    >
      <div className="space-y-6 p-5">
        <section className="grid gap-4 lg:grid-cols-[1fr_280px]">
          <div className="rounded-xl border border-white/10 bg-white/[0.025] p-4">
            <h3 className="font-medium text-white">Datos del inventario</h3>
            <dl className="mt-4 grid gap-x-6 gap-y-4 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-xs uppercase text-slate-500">Prestación</dt>
                <dd className="mt-1 text-slate-200">
                  {asset.service_description || "—"}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase text-slate-500">Organismo</dt>
                <dd className="mt-1 text-slate-200">
                  {asset.calibration_provider || "—"}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase text-slate-500">
                  Fecha informada en inventario
                </dt>
                <dd className="mt-1 text-slate-200">
                  {formatDate(asset.inventory_calibration_date)}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase text-slate-500">
                  Vencimiento preliminar
                </dt>
                <dd className="mt-1 text-slate-200">
                  {formatDate(asset.inventory_due_date)}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase text-slate-500">Fuente</dt>
                <dd className="mt-1 text-slate-200">
                  {asset.source_filename
                    ? `${asset.source_filename} · fila ${asset.source_row}`
                    : "Alta manual"}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase text-slate-500">Capacidades</dt>
                <dd className="mt-1 flex flex-wrap gap-2">
                  {(asset.capabilities || []).map((capability) => (
                    <span
                      key={capability.id}
                      className="rounded-full border border-white/10 bg-white/5 px-2 py-1 text-xs text-slate-300"
                    >
                      {FAMILY_LABELS[capability.magnitude] ||
                        capability.magnitude}
                      {capability.unit ? ` · ${capability.unit}` : ""}
                    </span>
                  ))}
                </dd>
              </div>
            </dl>
          </div>

          <div className="space-y-4 rounded-xl border border-white/10 bg-white/[0.025] p-4">
            <h3 className="font-medium text-white">Control del registro</h3>
            <Field label="Estado operativo">
              <select
                className={inputClass}
                value={asset.operational_status}
                disabled={busy}
                onChange={(event) =>
                  update({ operational_status: event.target.value })
                }
              >
                <option value="active">Activo</option>
                <option value="inactive">No activo</option>
                <option value="retired">Baja</option>
              </select>
            </Field>
            <Field label="Revisión">
              <select
                className={inputClass}
                value={asset.review_status}
                disabled={busy}
                onChange={(event) => update({ review_status: event.target.value })}
              >
                <option value="pending">Pendiente</option>
                <option value="verified">Verificado</option>
                <option value="needs_attention">Revisar coincidencia</option>
              </select>
            </Field>
            <p className="text-xs leading-5 text-slate-500">
              Verificar el activo no habilita automáticamente un certificado.
              Ambos controles deben estar aprobados.
            </p>
          </div>
        </section>

        <section className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="font-medium text-white">Capacidades metrológicas</h3>
              <p className="text-xs text-slate-500">
                El rango y el rol determinan en qué plantillas puede utilizarse el patrón.
              </p>
            </div>
            <button type="button" className="btn-ghost" onClick={() => setAddingCapability(true)}>
              <Plus size={15} />
              Agregar capacidad
            </button>
          </div>
          {(asset.capabilities || []).map((capability) => (
            <CapabilityEditor
              key={capability.id}
              capability={capability}
              family={primaryFamily(asset)}
              busy={busy}
              onSave={(patch) => saveCapability(capability, patch)}
            />
          ))}
          {addingCapability && (
            <CapabilityEditor
              key="new-capability"
              family={primaryFamily(asset)}
              busy={busy}
              isNew
              onSave={addCapability}
              onCancel={() => setAddingCapability(false)}
            />
          )}
        </section>

        <section>
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <h3 className="font-medium text-white">Certificados</h3>
              <p className="text-xs text-slate-500">
                Historial documental del activo. Los PDF son privados.
              </p>
            </div>
            <button
              type="button"
              onClick={onAddCertificate}
              className="btn-primary"
            >
              <Plus size={16} />
              Agregar certificado
            </button>
          </div>
          <div className="overflow-hidden rounded-xl border border-white/10">
            {certificates.length === 0 ? (
              <div className="px-5 py-10 text-center text-sm text-slate-500">
                Todavía no hay certificados cargados.
              </div>
            ) : (
              <div className="divide-y divide-white/10">
                {certificates.map((certificate) => {
                  const remaining = daysUntil(certificate.valid_until);
                  return (
                    <div
                      key={`${certificate.id}-${certificate.asset_role}`}
                      className="grid gap-3 px-4 py-3 text-sm md:grid-cols-[1.1fr_1fr_120px_150px_auto] md:items-center"
                    >
                      <div>
                        <div className="font-medium text-white">
                          {certificate.certificate_no}
                        </div>
                        <div className="text-xs text-slate-500">
                          {certificate.issuer || "Emisor sin informar"}
                          {certificate.results?.length
                            ? ` · ${certificate.results.length} resultado(s)`
                            : ""}
                        </div>
                      </div>
                      <div className="text-slate-300">
                        Calibración: {formatDate(certificate.calibration_date)}
                      </div>
                      <div>
                        <Badge
                          meta={
                            certificate.certificate_status === "verified"
                              ? REVIEW_META.verified
                              : certificate.certificate_status === "expired"
                                ? {
                                    label: "Vencido",
                                    className:
                                      "border-rose-500/30 bg-rose-500/10 text-rose-200",
                                  }
                                : REVIEW_META.pending
                          }
                        />
                      </div>
                      <div
                        className={
                          remaining !== null && remaining < 0
                            ? "text-rose-300"
                            : remaining !== null && remaining <= 90
                              ? "text-amber-300"
                              : "text-slate-300"
                        }
                      >
                        Vence: {formatDate(certificate.valid_until)}
                      </div>
                      <button
                        type="button"
                        disabled={!certificate.storage_path}
                        onClick={() => onOpenCertificate(certificate)}
                        className="btn-ghost disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <FileText size={15} />
                        Ver PDF
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </div>
    </Modal>
  );
}

export default function ReferenceStandardInstrumentsPage() {
  const { isAdmin, isLabManager, loading: permissionsLoading } =
    useLabPermissions();
  const canManage = isAdmin || isLabManager;

  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [migrationPending, setMigrationPending] = useState(false);
  const [query, setQuery] = useState("");
  const [familyFilter, setFamilyFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [reviewFilter, setReviewFilter] = useState("all");
  const [assetFormOpen, setAssetFormOpen] = useState(false);
  const [selectedAssetId, setSelectedAssetId] = useState(null);
  const [certificateAssetId, setCertificateAssetId] = useState(null);

  const load = useCallback(async () => {
    if (!canManage) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const rows = await listReferenceStandardAssets();
      setAssets(rows);
      setMigrationPending(false);
    } catch (error) {
      console.error("[ReferenceStandards] load", error);
      if (isReferenceStandardsMigrationMissing(error)) {
        setMigrationPending(true);
        setAssets([]);
      } else {
        setMessage({
          type: "error",
          text:
            error?.message ||
            "No se pudieron cargar los instrumentos patrones.",
        });
      }
    } finally {
      setLoading(false);
    }
  }, [canManage]);

  useEffect(() => {
    load();
  }, [load]);

  const selectedAsset =
    assets.find((asset) => asset.id === selectedAssetId) || null;
  const certificateAsset =
    assets.find((asset) => asset.id === certificateAssetId) || null;

  const metrics = useMemo(() => {
    const active = assets.filter(
      (asset) => asset.operational_status === "active"
    );
    let validCertificates = 0;
    let expiring = 0;
    let expired = 0;

    active.forEach((asset) => {
      const current = certificatesFor(asset)[0];
      if (!current) return;
      const remaining = daysUntil(current.valid_until);
      if (
        current.certificate_status === "expired"
        || (remaining !== null && remaining < 0)
      ) {
        expired += 1;
      } else if (current.certificate_status === "verified") {
        validCertificates += 1;
        if (remaining !== null && remaining <= 90) expiring += 1;
      }
    });

    return {
      total: assets.length,
      active: active.length,
      verified: validCertificates,
      expiring,
      expired,
      pending: assets.filter((asset) => asset.review_status !== "verified")
        .length,
    };
  }, [assets]);

  const filteredAssets = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase("es");
    return assets.filter((asset) => {
      const family = primaryFamily(asset);
      const matchesQuery =
        !needle ||
        [
          primaryCode(asset),
          asset.name,
          asset.brand_model,
          asset.serial,
          asset.service_description,
        ].some((value) =>
          String(value || "").toLocaleLowerCase("es").includes(needle)
        );
      return (
        matchesQuery &&
        (familyFilter === "all" || family === familyFilter) &&
        (statusFilter === "all" ||
          asset.operational_status === statusFilter) &&
        (reviewFilter === "all" || asset.review_status === reviewFilter)
      );
    });
  }, [assets, familyFilter, query, reviewFilter, statusFilter]);

  const handleCreateAsset = async (form) => {
    try {
      setBusy(true);
      setMessage(null);
      await createReferenceStandardAsset({
        asset: {
          name: form.name,
          asset_kind: form.asset_kind,
          operational_status: form.operational_status,
          review_status: "pending",
          brand_model: form.brand_model,
          serial: form.serial,
          service_description: form.service_description,
          calibration_frequency_months:
            form.calibration_frequency_months || null,
          calibration_provider: form.calibration_provider,
          estimated_lead_time: form.estimated_lead_time,
        },
        code: { code: form.code, family: form.family },
        capability: {
          magnitude: form.family,
          function_key: "manual_scope",
          function_label: form.service_description,
          capability_role: form.capability_role,
          range_min: form.range_min,
          range_max: form.range_max,
          unit: form.unit,
          resolution: form.resolution,
        },
      });
      setAssetFormOpen(false);
      setMessage({
        type: "ok",
        text: "El instrumento patrón fue creado y quedó pendiente de revisión.",
      });
      await load();
    } catch (error) {
      setMessage({
        type: "error",
        text: error?.message || "No se pudo crear el instrumento patrón.",
      });
    } finally {
      setBusy(false);
    }
  };

  const handleCreateCertificate = async ({ form, file }) => {
    if (!certificateAsset) return;
    try {
      setBusy(true);
      setMessage(null);
      const results = form.capability_id
        ? parseCertificateResultRows(form.result_rows, form)
        : [];
      await createReferenceStandardCertificate({
        assetId: certificateAsset.id,
        certificate: form,
        file,
        capabilityId: form.capability_id || null,
        results,
        referenceInputMode: form.reference_input_mode,
      });
      setCertificateAssetId(null);
      setMessage({
        type: "ok",
        text: "El certificado quedó guardado en el historial del patrón.",
      });
      await load();
    } catch (error) {
      setMessage({
        type: "error",
        text: error?.message || "No se pudo guardar el certificado.",
      });
    } finally {
      setBusy(false);
    }
  };

  const handleOpenCertificate = async (certificate) => {
    try {
      setBusy(true);
      const url = await getReferenceCertificateSignedUrl(
        certificate.storage_path
      );
      if (!url) throw new Error("El certificado no tiene un PDF asociado.");
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (error) {
      setMessage({
        type: "error",
        text: error?.message || "No se pudo abrir el certificado.",
      });
    } finally {
      setBusy(false);
    }
  };

  if (permissionsLoading) return null;

  if (!canManage) {
    return (
      <div className="mx-auto max-w-3xl py-10">
        <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-5 text-rose-200">
          Solo el jefe de laboratorio y los administradores pueden ingresar a
          Instrumentos Patrones.
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1500px] space-y-5 pb-16">
      <header className="flex flex-col gap-4 py-5 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-white">
            <CircleGauge className="text-emerald-400" size={27} />
            Instrumentos Patrones
          </h1>
          <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-400">
            Catálogo central de patrones, materiales de referencia, capacidades
            metrológicas y certificados vigentes.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={load} className="btn-ghost">
            <RefreshCw size={16} />
            Actualizar
          </button>
          <button
            type="button"
            onClick={() => setAssetFormOpen(true)}
            disabled={migrationPending}
            className="btn-primary"
          >
            <Plus size={16} />
            Nuevo instrumento patrón
          </button>
        </div>
      </header>

      {migrationPending && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-100">
          La interfaz ya está instalada, pero falta aplicar la migración de
          Instrumentos Patrones en Supabase.
        </div>
      )}

      {message && (
        <div
          className={`rounded-xl border p-4 text-sm ${
            message.type === "ok"
              ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-100"
              : "border-rose-500/30 bg-rose-500/10 text-rose-100"
          }`}
        >
          {message.text}
        </div>
      )}

      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
        <Metric icon={PackageCheck} label="Total inventario" value={metrics.total} />
        <Metric
          icon={CheckCircle2}
          label="Activos"
          value={metrics.active}
          tone="emerald"
        />
        <Metric
          icon={FileCheck2}
          label="Certificado vigente"
          value={metrics.verified}
          tone="sky"
        />
        <Metric
          icon={CalendarClock}
          label="Vencen en 90 días"
          value={metrics.expiring}
          tone="amber"
        />
        <Metric
          icon={AlertTriangle}
          label="Certificado vencido"
          value={metrics.expired}
          tone="rose"
        />
        <Metric
          icon={ShieldCheck}
          label="Pendientes de revisión"
          value={metrics.pending}
          tone="amber"
        />
      </section>

      <section className="rounded-xl border border-white/10 bg-white/[0.025] p-4">
        <div className="grid gap-3 lg:grid-cols-[minmax(260px,1fr)_190px_170px_190px]">
          <label className="relative">
            <Search
              size={17}
              className="pointer-events-none absolute left-3 top-2.5 text-slate-500"
            />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              className={`${inputClass} pl-9`}
              placeholder="Buscar por código, equipo, modelo o serie…"
            />
          </label>
          <label className="relative">
            <Filter
              size={15}
              className="pointer-events-none absolute left-3 top-2.5 text-slate-500"
            />
            <select
              className={`${inputClass} pl-9`}
              value={familyFilter}
              onChange={(event) => setFamilyFilter(event.target.value)}
            >
              <option value="all">Todas las magnitudes</option>
              {FAMILY_OPTIONS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <select
            className={inputClass}
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option value="all">Todos los estados</option>
            <option value="active">Activos</option>
            <option value="inactive">No activos</option>
            <option value="retired">Baja</option>
          </select>
          <select
            className={inputClass}
            value={reviewFilter}
            onChange={(event) => setReviewFilter(event.target.value)}
          >
            <option value="all">Todas las revisiones</option>
            <option value="verified">Verificados</option>
            <option value="pending">Pendientes</option>
            <option value="needs_attention">Con observaciones</option>
          </select>
        </div>
      </section>

      <section className="overflow-hidden rounded-xl border border-white/10 bg-slate-950/30">
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-white/10 bg-white/[0.04] text-xs uppercase tracking-wide text-slate-400">
              <tr>
                <th className="px-4 py-3">Código / Magnitud</th>
                <th className="px-4 py-3">Equipo o material</th>
                <th className="px-4 py-3">Serie / Lote</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3">Certificado actual</th>
                <th className="px-4 py-3">Vigencia</th>
                <th className="w-12 px-4 py-3">
                  <span className="sr-only">Abrir</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-4 py-16 text-center text-slate-400">
                    <Loader2 className="mx-auto mb-2 animate-spin" size={22} />
                    Cargando instrumentos patrones…
                  </td>
                </tr>
              ) : filteredAssets.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-16 text-center text-slate-500">
                    <FlaskConical className="mx-auto mb-2 opacity-60" size={28} />
                    No hay instrumentos que coincidan con los filtros.
                  </td>
                </tr>
              ) : (
                filteredAssets.map((asset) => {
                  const certificate = certificatesFor(asset)[0] || null;
                  const remaining = daysUntil(certificate?.valid_until);
                  return (
                    <tr
                      key={asset.id}
                      onClick={() => setSelectedAssetId(asset.id)}
                      className="cursor-pointer transition hover:bg-white/[0.035]"
                    >
                      <td className="px-4 py-3">
                        <div className="font-semibold text-white">
                          {primaryCode(asset)}
                        </div>
                        <div className="text-xs text-emerald-300/80">
                          {FAMILY_LABELS[primaryFamily(asset)] ||
                            primaryFamily(asset) ||
                            "Sin magnitud"}
                        </div>
                      </td>
                      <td className="max-w-md px-4 py-3">
                        <div className="font-medium text-slate-100">
                          {asset.name}
                        </div>
                        <div className="mt-0.5 truncate text-xs text-slate-500">
                          {asset.brand_model || asset.service_description || "—"}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-300">
                        {asset.serial || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col items-start gap-1.5">
                          <Badge meta={STATUS_META[asset.operational_status]} />
                          <Badge meta={REVIEW_META[asset.review_status]} />
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {certificate ? (
                          <>
                            <div className="font-medium text-slate-200">
                              {certificate.certificate_no}
                            </div>
                            <div className="text-xs text-slate-500">
                              {certificate.issuer || "Emisor sin informar"}
                            </div>
                          </>
                        ) : (
                          <span className="text-slate-500">Sin certificado</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {certificate?.valid_until ? (
                          <div
                            className={
                              remaining < 0
                                ? "text-rose-300"
                                : remaining <= 90
                                  ? "text-amber-300"
                                  : "text-slate-300"
                            }
                          >
                            {formatDate(certificate.valid_until)}
                            <div className="text-xs opacity-70">
                              {remaining < 0
                                ? `Vencido hace ${Math.abs(remaining)} días`
                                : `${remaining} días restantes`}
                            </div>
                          </div>
                        ) : (
                          <div className="text-slate-500">
                            Inventario: {formatDate(asset.inventory_due_date)}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-500">
                        <ChevronRight size={18} />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {!loading && (
          <footer className="flex items-center justify-between border-t border-white/10 px-4 py-3 text-xs text-slate-500">
            <span>
              Mostrando {filteredAssets.length} de {assets.length} registros
            </span>
            <span>Los registros importados requieren revisión documental.</span>
          </footer>
        )}
      </section>

      {assetFormOpen && (
        <AssetFormModal
          busy={busy}
          onClose={() => setAssetFormOpen(false)}
          onSubmit={handleCreateAsset}
        />
      )}

      {selectedAsset && (
        <AssetDetailModal
          asset={selectedAsset}
          busy={busy}
          onClose={() => setSelectedAssetId(null)}
          onReload={load}
          onAddCertificate={() => setCertificateAssetId(selectedAsset.id)}
          onOpenCertificate={handleOpenCertificate}
        />
      )}

      {certificateAsset && (
        <CertificateFormModal
          asset={certificateAsset}
          busy={busy}
          onClose={() => setCertificateAssetId(null)}
          onSubmit={handleCreateCertificate}
        />
      )}
    </div>
  );
}
